import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  alpha,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from '@mui/material';
import {
  DeleteOutline as DeleteIcon,
  PlaceOutlined as SiteIcon,
} from '@mui/icons-material';
import EntityDetailRow from '../common/EntityDetailRow';
import { formatSiteLocation } from '../../helpers/collection-site.helpers';
import { CollectionSiteSchema, CollectionSiteType } from '../../types/collection-site.type';

export interface CollectionSiteDeleteDialogProps {
  open: boolean;
  site: CollectionSiteSchema | null;
  loading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export function CollectionSiteDeleteDialog({
  open,
  site,
  loading = false,
  onClose,
  onConfirm,
}: CollectionSiteDeleteDialogProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  if (!site) return null;

  const displayName = site.data?.name || t('collectionSites.siteDetails');
  const siteType = site.data?.type as CollectionSiteType | undefined;
  const typeLabel = siteType
    ? t(`collectionSites.types.${siteType}`, { defaultValue: siteType })
    : t('common.notAvailable');
  const locationLabel = formatSiteLocation(site) || t('collectionSites.card.locationNotSet');
  const contactName = site.data?.contact_info?.name?.trim();

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 2.5,
          py: 2,
          bgcolor: alpha(theme.palette.error.main, 0.08),
          borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.error.main, 0.14),
              color: theme.palette.error.main,
              flexShrink: 0,
            }}
          >
            <DeleteIcon sx={{ fontSize: 24 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="error.main" sx={{ fontWeight: 600 }}>
              {t('collectionSites.deleteDialog.caption')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25, m: 0 }}>
              {t('collectionSites.deleteDialog.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} noWrap>
              {displayName}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, py: 2.5 }}>
        <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('collectionSites.deleteDialog.warningTitle')}
          </Typography>
          <Typography variant="body2">{t('collectionSites.deleteDialog.warningDetail')}</Typography>
        </Alert>

        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
          {t('collectionSites.deleteDialog.detailsTitle')}
        </Typography>

        <Box
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '12px',
            overflow: 'hidden',
            bgcolor: alpha(theme.palette.primary.main, 0.02),
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.25,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderBottom: `1px solid ${theme.palette.divider}`,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
            }}
          >
            <SiteIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ flex: 1 }}>
              {displayName}
            </Typography>
            <Chip size="small" label={typeLabel} color="primary" variant="outlined" />
          </Box>

          <Box sx={{ px: 2, py: 1 }}>
            <EntityDetailRow label={t('common.type')}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {typeLabel}
              </Typography>
            </EntityDetailRow>
            <EntityDetailRow label={t('collectionSites.card.location')}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {locationLabel}
              </Typography>
            </EntityDetailRow>
            {site.area != null ? (
              <EntityDetailRow label={t('common.area')}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {site.area}
                </Typography>
              </EntityDetailRow>
            ) : null}
            {contactName ? (
              <EntityDetailRow label={t('collectionSites.contactName')}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {contactName}
                </Typography>
              </EntityDetailRow>
            ) : null}
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {t('collectionSites.deleteDialog.description')}
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          gap: 1,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Button onClick={onClose} disabled={loading} sx={{ textTransform: 'none' }}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
          sx={{ textTransform: 'none', px: 2.5 }}
        >
          {loading ? t('collectionSites.deleteDialog.deleting') : t('collectionSites.deleteDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
